import React, { useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import {
    CalendarCancel20Regular,
    ClipboardError20Regular,
    Wrench20Regular,
    Warning20Regular,
    LightbulbFilament20Regular,
    DocumentSearch20Regular,
    Checkmark20Regular,
    Dismiss20Regular,
    ArrowUndo20Regular,
} from '@fluentui/react-icons';

import { SyncAnalysisFinding } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import { SyncAnalysisFindingDecisionEnum } from '../../../../models/Enums/SyncAnalysisFindingDecisionEnum';
import * as ComponentFunctions from './ConsumptionSyncProcessIAAnalysis.Functions';

const TYPE_ICONS: Record<SyncAnalysisFindingTypeEnum, React.ReactNode> = {
    [SyncAnalysisFindingTypeEnum.VisitWithoutConsumption]: <CalendarCancel20Regular />,
    [SyncAnalysisFindingTypeEnum.UnsettledFormItem]: <ClipboardError20Regular />,
    [SyncAnalysisFindingTypeEnum.MisconfiguredConcept]: <Wrench20Regular />,
    [SyncAnalysisFindingTypeEnum.ContradictoryConfiguration]: <Warning20Regular />,
};

interface IProps {
    finding: SyncAnalysisFinding;
    /** Registrar la decisión del analista. Sin esto la tarjeta es sólo lectura. */
    onDecide?: (finding: SyncAnalysisFinding, decision: SyncAnalysisFindingDecisionEnum, reason?: string) => void;
    /** Hay una decisión en viaje sobre esta tarjeta. */
    isDeciding?: boolean;
    /** La última decisión sobre esta tarjeta falló. */
    decisionError?: string;
}

/**
 * Un hallazgo de Alphi con su evidencia, su recomendación y la decisión del
 * analista: confirmarlo, descartarlo con un motivo, o volverlo a pendiente.
 */
export const SyncAnalysisFindingCard = ({ finding, onDecide, isDeciding, decisionError }: IProps) => {
    const descriptor = ComponentFunctions.getFindingTypeDescriptor(finding.findingType);
    const typeLabel = ComponentFunctions.getFindingTypeLabel(finding.findingType);
    const typeClass = 'csa-finding--type' + finding.findingType;
    const decision = ComponentFunctions.getDecisionDescriptor(finding.decision);
    const pending = ComponentFunctions.isFindingPending(finding);

    // Qué decisión se está por tomar: abre el campo del motivo antes de confirmar.
    const [choosing, setChoosing] = useState<SyncAnalysisFindingDecisionEnum | null>(null);
    const [reason, setReason] = useState<string>('');

    // La confianza llega como entero porcentual; se acota por si el servidor
    // devolviera algo fuera de rango, para que la barra no se desborde.
    const confidence = Math.max(0, Math.min(100, finding.confidence ?? 0));

    const references = [
        finding.protocolVisitEntryId !== null && finding.protocolVisitEntryId !== undefined
            ? { label: 'Visita', value: '#' + finding.protocolVisitEntryId }
            : null,
        finding.contractConceptId !== null && finding.contractConceptId !== undefined
            ? { label: 'Concepto', value: '#' + finding.contractConceptId }
            : null,
        finding.recommendedContractConceptId !== null && finding.recommendedContractConceptId !== undefined
            ? { label: 'Concepto sugerido', value: '#' + finding.recommendedContractConceptId }
            : null,
        finding.formItemDescription ? { label: 'Ítem', value: finding.formItemDescription } : null,
    ].filter(Boolean) as { label: string; value: string }[];

    const startDecision = (next: SyncAnalysisFindingDecisionEnum) => {
        setChoosing(next);
        setReason('');
    };

    const confirmDecision = () => {
        if (choosing === null || !onDecide) return;
        onDecide(finding, choosing, reason.trim() || undefined);
        setChoosing(null);
    };

    const reopen = () => onDecide && onDecide(finding, SyncAnalysisFindingDecisionEnum.Pending);

    return (
        <li className={'csa-finding ' + typeClass + (pending ? '' : ' csa-finding--decided')}>
            <div className="csa-finding__head">
                <span className="csa-finding__icon">
                    {TYPE_ICONS[finding.findingType] ?? <DocumentSearch20Regular />}
                </span>
                <div className="csa-finding__headText">
                    <span className="csa-finding__type" title={descriptor?.description}>
                        {typeLabel}
                    </span>
                    <span className="csa-finding__subject">
                        {finding.subjectDescription || descriptor?.anchor || '—'}
                    </span>
                </div>
                <div className="csa-finding__flags">
                    {!pending && (
                        <span className={'csa-tag csa-tag--' + decision.variant} title={decision.description}>
                            {decision.label}
                        </span>
                    )}
                    {finding.isNew && <span className="csa-tag csa-tag--new">Nuevo</span>}
                    {finding.reopened && <span className="csa-tag csa-tag--reopened">Reabierto</span>}
                </div>
            </div>

            <div className="csa-finding__scores">
                <span className="csa-finding__severity" title="Prioridad asignada por el análisis">
                    Severidad {finding.severity}
                </span>
                <span className="csa-finding__confidence" title="Confianza del análisis sobre este hallazgo">
                    <span className="csa-finding__confidenceBar">
                        <span
                            className="csa-finding__confidenceFill"
                            style={{ width: confidence + '%' }}
                        />
                    </span>
                    {confidence}%
                </span>
            </div>

            {references.length > 0 && (
                <dl className="csa-finding__refs">
                    {references.map(reference => (
                        <div className="csa-finding__ref" key={reference.label}>
                            <dt>{reference.label}</dt>
                            <dd>{reference.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {finding.evidence && (
                <div className="csa-finding__block">
                    <span className="csa-finding__blockTitle">
                        <DocumentSearch20Regular />
                        Evidencia
                    </span>
                    <p className="csa-finding__blockText">{finding.evidence}</p>
                </div>
            )}

            {finding.recommendation && (
                <div className="csa-finding__block csa-finding__block--recommendation">
                    <span className="csa-finding__blockTitle">
                        <LightbulbFilament20Regular />
                        Recomendación
                    </span>
                    <p className="csa-finding__blockText">{finding.recommendation}</p>
                </div>
            )}

            {/* ── Decisión del analista ── */}
            {onDecide && (
                <div className="csa-decision">
                    {isDeciding && (
                        <span className="csa-decision__busy"><Spinner size="extra-tiny" /> Guardando la decisión...</span>
                    )}

                    {!isDeciding && !pending && (
                        <>
                            <div className="csa-decision__current">
                                <span className={'csa-tag csa-tag--' + decision.variant}>{decision.label}</span>
                                {finding.decisionReason
                                    ? <span className="csa-decision__reason">“{finding.decisionReason}”</span>
                                    : <span className="csa-decision__reason csa-decision__reason--empty">Sin motivo</span>}
                            </div>
                            <button type="button" className="csa-decision__btn csa-decision__btn--text" onClick={reopen}>
                                <ArrowUndo20Regular /> Volver a pendiente
                            </button>
                        </>
                    )}

                    {!isDeciding && pending && choosing === null && (
                        <div className="csa-decision__actions">
                            <button
                                type="button"
                                className="csa-decision__btn csa-decision__btn--accept"
                                title={ComponentFunctions.getDecisionDescriptor(SyncAnalysisFindingDecisionEnum.Accepted).description}
                                onClick={() => startDecision(SyncAnalysisFindingDecisionEnum.Accepted)}
                            >
                                <Checkmark20Regular /> Confirmar
                            </button>
                            <button
                                type="button"
                                className="csa-decision__btn csa-decision__btn--discard"
                                title={ComponentFunctions.getDecisionDescriptor(SyncAnalysisFindingDecisionEnum.Discarded).description}
                                onClick={() => startDecision(SyncAnalysisFindingDecisionEnum.Discarded)}
                            >
                                <Dismiss20Regular /> Descartar
                            </button>
                        </div>
                    )}

                    {!isDeciding && pending && choosing !== null && (
                        <div className="csa-decision__form">
                            <span className="csa-decision__formTitle">
                                {choosing === SyncAnalysisFindingDecisionEnum.Accepted ? 'Confirmar el hallazgo' : 'Descartar el hallazgo'}
                            </span>
                            <textarea
                                className="csa-decision__reasonInput"
                                placeholder={choosing === SyncAnalysisFindingDecisionEnum.Accepted
                                    ? 'Qué se va a hacer (opcional)'
                                    : 'Por qué no aplica (opcional, pero ayuda a la próxima corrida)'}
                                value={reason}
                                rows={2}
                                maxLength={500}
                                onChange={event => setReason(event.target.value)}
                                autoFocus
                            />
                            <div className="csa-decision__actions">
                                <button type="button" className="csa-decision__btn csa-decision__btn--text" onClick={() => setChoosing(null)}>Cancelar</button>
                                <button
                                    type="button"
                                    className={'csa-decision__btn ' + (choosing === SyncAnalysisFindingDecisionEnum.Accepted ? 'csa-decision__btn--accept' : 'csa-decision__btn--discard')}
                                    onClick={confirmDecision}
                                >
                                    {choosing === SyncAnalysisFindingDecisionEnum.Accepted ? 'Confirmar' : 'Descartar'}
                                </button>
                            </div>
                        </div>
                    )}

                    {decisionError && <span className="csa-decision__error" role="alert">{decisionError}</span>}
                </div>
            )}
        </li>
    );
};

export default SyncAnalysisFindingCard;
